import type { IconType } from '@assembly-js/design-system';
import { t } from 'i18next';

import {
  API_SETTINGS_PAGE,
  ASSISTANT_SETTINGS_PAGE,
  AUTOMATIONS_PAGE,
  BILLING_PAGE,
  CONNECTIONS_PAGE,
  DANGER_ZONE_PAGE,
  DOMAIN_PAGE,
  GENERAL_SETTINGS_PAGE,
  NOTIFICATIONS_SETTINGS_PAGE,
  PAYMENTS_PAGE,
  PERSONAL_CONNECTIONS_PAGE,
  PLAN_PAGE,
  PROFILE_PAGE,
  SETTINGS_EVENTS_PAGE,
  SETTINGS_PAGE_ROOT_PATH,
  SETTINGS_REFERRALS_PAGE,
  SETTINGS_TEAM_PAGE,
} from 'src/constants';
import type { FlagContextType } from 'src/context/flagsContext';
import type { StudioEntitlements } from 'src/hooks/usePlanStatusHook';
import type { RouteAccess } from 'src/routes/routeAccess';
import type { StudioFeature } from 'src/routes/settings/plans/components/studio/studioFeatureGating';
import {
  ClientSettingsPage,
  getClientSettingsPath,
  getClientSettingsTitle,
  isClientSettingsPath,
} from 'src/utils/clientSettingsPaths';

export type SettingsSection =
  | 'you'
  | 'workspace'
  | 'clientExperience'
  | 'ai'
  | 'developers'
  | 'billing';

export type SettingsRouteAccess = Pick<
  RouteAccess,
  | 'allowProfilePage'
  | 'allowNotificationSettings'
  | 'allowSettingsReferralPage'
  | 'allowSettingsMainPage'
  | 'allowSettingsMembersPage'
  | 'allowSettingsAuditLog'
  | 'allowSettingsDangerZone'
  | 'allowSettingsClientAuth'
  | 'allowSettingsClientNotificationsPage'
  | 'allowSettingsDomainPage'
  | 'allowSettingsConnectionsPage'
  | 'allowSettingsPersonalConnectionsPage'
  | 'allowSettingsAssistant'
  | 'allowSettingsApiPage'
  | 'allowSettingsPlanPage'
  | 'allowSettingsBillingPage'
  | 'allowSettingsModulePayments'
>;

/** Everything a page's visibility rule or plan badge may read. */
export interface SettingsVisibilityContext {
  routeAccess: SettingsRouteAccess;
  isClient: boolean;
  hasAutomationsAccess: boolean;
  canManageCustomFields: boolean;
  /** Clients of a workspace with companies turned off don't get workspace-level pages. */
  hideWorkspacePagesFromClient: boolean;
  allowAuditLog: boolean;
  planStatus: {
    isStarterPlan: boolean;
    isTrialing: boolean;
    requireProUpgrade: boolean;
    studio: StudioEntitlements;
  };
  flags: Pick<
    FlagContextType,
    'EnableAuditLogs' | 'EnableNewCustomFields' | 'EnableStudioSidebar'
  >;
}

export interface SettingsPage {
  id: string;
  section: SettingsSection;
  /** Sentence case. A function when the label carries the workspace's client term. */
  label: string | ((clientTerm: string) => string);
  /**
   * The one-liner under the page title. A function when it comes from
   * translations, so it resolves at render rather than before i18n loads.
   */
  description: string | (() => string);
  /** Unchanged for renamed pages, so bookmarks and help-center links keep working. */
  path: string;
  /**
   * Set when the URL carries the workspace's client term
   * (`/settings/contact-notifications`); `path` is then resolved per workspace.
   */
  clientTermPage?: ClientSettingsPage;
  /** Old URLs that should land on this page while `new-settings` is on. */
  legacyPaths?: string[];
  /** Extra phrases search should match, e.g. 'magic link'. */
  keywords?: string[];
  /**
   * The entitlement behind the Pro / Advanced tag, shown while the plan lacks
   * it. The tier comes from the entitlement matrix rather than a hardcoded
   * label so it can't drift from what the plan unlocks; Classic workspaces keep
   * their legacy rule via `showOnClassic`.
   */
  planBadge?: {
    feature: StudioFeature;
    showOnClassic?: (ctx: SettingsVisibilityContext) => boolean;
  };
  iconName: IconType;
  /** Omitted when every user who can open Settings sees the page. */
  isVisible?: (ctx: SettingsVisibilityContext) => boolean;
}

/** A page with its label, description and path resolved for one workspace. */
export type ResolvedSettingsPage = SettingsPage & {
  label: string;
  description: string;
  path: string;
};

interface SettingsSectionGroup {
  id: SettingsSection;
  title: string;
  pages: ResolvedSettingsPage[];
}

const SETTINGS_SECTIONS: ReadonlyArray<{
  id: SettingsSection;
  title: string | (() => string);
}> = [
  { id: 'you', title: 'You' },
  { id: 'workspace', title: 'Workspace' },
  {
    id: 'clientExperience',
    title: () => t(($) => $.navigation.client_experience),
  },
  { id: 'ai', title: 'AI' },
  { id: 'developers', title: 'Developers' },
  { id: 'billing', title: 'Billing' },
];

const DEFAULT_CLIENT_TERM = 'client';

// Order within each section is the sidebar order.
export const SETTINGS_PAGES: ReadonlyArray<SettingsPage> = [
  {
    id: 'profile',
    section: 'you',
    label: 'Profile',
    description: 'Manage your account and personal information.',
    path: PROFILE_PAGE.path,
    iconName: 'circle-user',
  },
  {
    id: 'notifications',
    section: 'you',
    label: 'Notifications',
    description: 'Choose what you’re notified about and where.',
    path: NOTIFICATIONS_SETTINGS_PAGE.path,
    iconName: 'bell',
    isVisible: (ctx) => ctx.routeAccess.allowNotificationSettings,
  },
  {
    id: 'referrals',
    section: 'you',
    label: 'Referrals',
    description: SETTINGS_REFERRALS_PAGE.description,
    path: SETTINGS_REFERRALS_PAGE.path,
    iconName: 'gift',
    isVisible: (ctx) => ctx.routeAccess.allowSettingsReferralPage,
  },
  {
    id: 'general',
    section: 'workspace',
    label: 'General',
    description: 'Business details for billing and compliance.',
    path: GENERAL_SETTINGS_PAGE.path,
    iconName: 'book',
    isVisible: (ctx) =>
      ctx.routeAccess.allowSettingsMainPage &&
      !ctx.hideWorkspacePagesFromClient,
  },
  {
    // Team leaves Settings for internal users on the Studio sidebar, which
    // carries it in the main nav. Clients and the Classic sidebar have no other
    // entry point, so they keep it here.
    id: 'team',
    section: 'workspace',
    label: 'Team',
    description: 'The people in your workspace.',
    path: SETTINGS_TEAM_PAGE.path,
    iconName: 'people-group',
    isVisible: (ctx) =>
      ctx.routeAccess.allowSettingsMembersPage &&
      !ctx.hideWorkspacePagesFromClient &&
      (ctx.isClient || !ctx.flags.EnableStudioSidebar),
  },
  {
    id: 'customFields',
    section: 'workspace',
    label: 'Custom fields',
    description: 'Extra fields to track on each record.',
    path: `${SETTINGS_PAGE_ROOT_PATH}/custom-fields`,
    iconName: 'message-pen',
    isVisible: (ctx) =>
      ctx.flags.EnableNewCustomFields &&
      !ctx.isClient &&
      ctx.canManageCustomFields,
  },
  {
    // Automations keeps its legacy page; the entry only places it in the
    // sidebar. The Classic sidebar lists it in the main nav instead.
    id: 'automations',
    section: 'workspace',
    label: 'Automations',
    description: 'Trigger messages, tasks, and updates automatically.',
    path: `${SETTINGS_PAGE_ROOT_PATH}${AUTOMATIONS_PAGE.path}`,
    iconName: 'bolt',
    planBadge: { feature: 'automations' },
    isVisible: (ctx) =>
      ctx.flags.EnableStudioSidebar &&
      !ctx.isClient &&
      ctx.hasAutomationsAccess,
  },
  {
    id: 'auditLog',
    section: 'workspace',
    label: 'Audit log',
    description: 'Track changes and activity from the past 12 months.',
    path: SETTINGS_EVENTS_PAGE.path,
    iconName: 'calendar-pen',
    planBadge: {
      feature: 'auditLog',
      showOnClassic: (ctx) => !ctx.allowAuditLog,
    },
    isVisible: (ctx) =>
      ctx.flags.EnableAuditLogs && ctx.routeAccess.allowSettingsAuditLog,
  },
  {
    id: 'dangerZone',
    section: 'workspace',
    label: 'Danger zone',
    description: 'Leave the workspace, or delete it.',
    path: DANGER_ZONE_PAGE.path,
    iconName: 'diamond-exclamation',
    isVisible: (ctx) => ctx.routeAccess.allowSettingsDangerZone,
  },
  {
    id: 'authentication',
    section: 'clientExperience',
    label: 'Authentication',
    description: () =>
      t(($) => $.page_settings.page_descriptions.authentication),
    path: getClientSettingsPath(DEFAULT_CLIENT_TERM, 'authentication'),
    clientTermPage: 'authentication',
    iconName: 'fingerprint',
    isVisible: (ctx) => ctx.routeAccess.allowSettingsClientAuth,
  },
  {
    // Split out of Authentication, so it shares that page's permission.
    id: 'selfServeSignUp',
    section: 'clientExperience',
    label: 'Self-serve sign up',
    description: () =>
      t(($) => $.page_settings.page_descriptions.self_serve_sign_up),
    path: `${SETTINGS_PAGE_ROOT_PATH}/self-serve-sign-up`,
    iconName: 'display',
    isVisible: (ctx) => ctx.routeAccess.allowSettingsClientAuth,
  },
  {
    id: 'clientNotifications',
    section: 'clientExperience',
    label: (clientTerm) => getClientSettingsTitle(clientTerm, 'notifications'),
    description: () =>
      t(($) => $.page_settings.page_descriptions.client_notifications),
    path: getClientSettingsPath(DEFAULT_CLIENT_TERM, 'notifications'),
    clientTermPage: 'notifications',
    iconName: 'envelope',
    isVisible: (ctx) => ctx.routeAccess.allowSettingsClientNotificationsPage,
  },
  {
    id: 'customDomain',
    section: 'clientExperience',
    label: 'Custom domain',
    description: 'Your portal address, and how it appears outside itself.',
    path: DOMAIN_PAGE.path,
    iconName: 'globe',
    planBadge: {
      feature: 'customDomain',
      showOnClassic: (ctx) =>
        ctx.planStatus.isStarterPlan || ctx.planStatus.requireProUpgrade,
    },
    isVisible: (ctx) => ctx.routeAccess.allowSettingsDomainPage,
  },
  {
    id: 'connectors',
    section: 'ai',
    label: 'Connectors',
    description: 'Two-way syncs with the tools you run on.',
    path: CONNECTIONS_PAGE.path,
    iconName: 'diagram-project',
    isVisible: (ctx) => ctx.routeAccess.allowSettingsConnectionsPage,
  },
  {
    id: 'personalConnections',
    section: 'ai',
    label: PERSONAL_CONNECTIONS_PAGE.label,
    description: PERSONAL_CONNECTIONS_PAGE.description,
    path: PERSONAL_CONNECTIONS_PAGE.path,
    iconName: 'diagram-project',
    isVisible: (ctx) => ctx.routeAccess.allowSettingsPersonalConnectionsPage,
  },
  {
    id: 'assistant',
    section: 'ai',
    label: 'Assistant',
    description: 'Your AI colleague for internal chat.',
    path: ASSISTANT_SETTINGS_PAGE.path,
    iconName: 'AssemblyLogo',
    isVisible: (ctx) => ctx.routeAccess.allowSettingsAssistant,
  },
  {
    // Moves off Profile, so it keeps Profile's audience: every plan, every user.
    id: 'mcp',
    section: 'ai',
    label: 'MCP',
    description: 'Connect AI tools to Assembly with MCP.',
    path: `${SETTINGS_PAGE_ROOT_PATH}/mcp`,
    iconName: 'diamonds-4',
    isVisible: (ctx) => ctx.routeAccess.allowProfilePage,
  },
  {
    id: 'apiKeys',
    section: 'developers',
    label: 'API keys',
    description: 'Let a server authenticate with the Assembly REST API.',
    path: API_SETTINGS_PAGE.path,
    iconName: 'gear-api',
    planBadge: {
      feature: 'api',
      showOnClassic: (ctx) =>
        ctx.planStatus.isStarterPlan || ctx.planStatus.isTrialing,
    },
    isVisible: (ctx) => ctx.routeAccess.allowSettingsApiPage,
  },
  {
    // Split out of API keys, so it shares that page's permission.
    id: 'webhooks',
    section: 'developers',
    label: 'Webhooks',
    description: 'A POST request to your endpoint when something changes.',
    path: `${SETTINGS_PAGE_ROOT_PATH}/webhooks`,
    iconName: 'webhook',
    isVisible: (ctx) => ctx.routeAccess.allowSettingsApiPage,
  },
  {
    id: 'planAndUsage',
    section: 'billing',
    label: 'Plan & usage',
    description: 'Your plan, seats, credits and payment method.',
    path: PLAN_PAGE.path,
    iconName: 'tag',
    isVisible: (ctx) => ctx.routeAccess.allowSettingsPlanPage,
  },
  {
    // A client's own saved cards and bank accounts, not the workspace's
    // subscription payment method, so it can't fold into Plan & usage.
    id: 'paymentMethods',
    section: 'billing',
    label: 'Payment methods',
    description: 'Your saved cards and bank accounts.',
    path: BILLING_PAGE.path,
    iconName: 'wallet',
    isVisible: (ctx) => ctx.routeAccess.allowSettingsBillingPage,
  },
  {
    id: 'payoutAccount',
    section: 'billing',
    label: 'Payout account',
    description: () =>
      t(($) => $.page_settings.page_descriptions.payout_account),
    path: PAYMENTS_PAGE.path,
    iconName: 'credit-card',
    isVisible: (ctx) => ctx.routeAccess.allowSettingsModulePayments,
  },
];

const resolveText = (text: string | (() => string)): string =>
  typeof text === 'function' ? text() : text;

const resolveSettingsPage = (
  page: SettingsPage,
  clientTerm: string,
): ResolvedSettingsPage => ({
  ...page,
  label: typeof page.label === 'function' ? page.label(clientTerm) : page.label,
  description: resolveText(page.description),
  path: page.clientTermPage
    ? getClientSettingsPath(clientTerm, page.clientTermPage)
    : page.path,
});

export const isSettingsPageVisible = (
  page: SettingsPage,
  ctx: SettingsVisibilityContext,
): boolean => page.isVisible?.(ctx) ?? true;

const trimTrailingSlash = (pathname: string) =>
  pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

/** Whether `pathname` is exactly this page, not one of its sub-pages. */
const isExactSettingsPagePath = (
  page: SettingsPage,
  pathname: string,
  clientTerm: string,
): boolean => {
  if (page.clientTermPage) {
    return isClientSettingsPath(pathname, clientTerm, page.clientTermPage);
  }
  const path = trimTrailingSlash(pathname);
  // The bare /settings route renders General.
  return (
    path === page.path ||
    (page.path === GENERAL_SETTINGS_PAGE.path &&
      path === SETTINGS_PAGE_ROOT_PATH)
  );
};

/** Whether `pathname` is this page or one of its sub-pages. */
export const isSettingsPageActive = (
  page: SettingsPage,
  pathname: string,
  clientTerm: string,
): boolean =>
  isExactSettingsPagePath(page, pathname, clientTerm) ||
  (!page.clientTermPage && pathname.startsWith(`${page.path}/`));

/** The page rendered at exactly `pathname`; sub-pages keep their own route header. */
export const findSettingsPageByPath = (
  pathname: string,
  clientTerm: string,
): ResolvedSettingsPage | undefined => {
  const page = SETTINGS_PAGES.find((candidate) =>
    isExactSettingsPagePath(candidate, pathname, clientTerm),
  );
  return page ? resolveSettingsPage(page, clientTerm) : undefined;
};

/** The page an old URL now lands on, if `pathname` is one of its `legacyPaths`. */
export const findSettingsPageByLegacyPath = (
  pathname: string,
  clientTerm: string,
): ResolvedSettingsPage | undefined => {
  const page = SETTINGS_PAGES.find((candidate) =>
    candidate.legacyPaths?.includes(pathname),
  );
  return page ? resolveSettingsPage(page, clientTerm) : undefined;
};

export const getVisibleSettingsSections = (
  ctx: SettingsVisibilityContext,
  clientTerm: string,
  pages: ReadonlyArray<SettingsPage> = SETTINGS_PAGES,
): SettingsSectionGroup[] =>
  SETTINGS_SECTIONS.map((section) => ({
    id: section.id,
    title: resolveText(section.title),
    pages: pages
      .filter(
        (page) =>
          page.section === section.id && isSettingsPageVisible(page, ctx),
      )
      .map((page) => resolveSettingsPage(page, clientTerm)),
  })).filter((section) => section.pages.length > 0);
